import {useEffect, useState} from 'react';
import {nanoid} from 'nanoid';
import {useSession} from '../../firebase/UserProvider';
import {apiRemoveReportSegmentTag, apiTagReportSegment} from '../../utils/api/tags';

/**
 * Tags for one report paragraph (Products search card), keyed by the stable
 * report_segments.segment_id. Same per-user vote model as corpus tags: createTag/voteTag
 * add the viewer's vote; voting an already-voted tag removes the vote.
 *
 * Disabled (all ops no-op) when reportSegmentId is null — the router hook always calls it.
 */
export default function useReportSegmentTags({reportSegmentId, initialTags}) {
    // Null-safe: useSession() is undefined outside UserProvider (e.g. unit tests).
    const user = useSession()?.user;
    const [tags, setTags] = useState(Array.isArray(initialTags) ? initialTags : []);
    const [isPosting, setIsPosting] = useState(false);
    const [loadingTagOps, setLoadingTagOps] = useState({});

    useEffect(() => {
        if (Array.isArray(initialTags)) setTags(initialTags);
    }, [initialTags, reportSegmentId]);

    const enabled = reportSegmentId != null && !!user?.uid;

    async function createTag(tagName, presetOperationId) {
        const name = (tagName || '').trim();
        if (!name || !enabled) return;
        const lower = name.toLowerCase();
        const operationId = presetOperationId || nanoid();
        setIsPosting(true);
        // Optimistic: add the tag (or the viewer's vote on it) immediately.
        setTags(prev => {
            const current = Array.isArray(prev) ? prev.map(x => ({...x})) : [];
            const idx = current.findIndex(x => (x.name || '').toLowerCase() === lower);
            if (idx === -1) return [{name, voteCount: 1, operationId}, ...current];
            if (current[idx].operationId) return current; // already voted
            current[idx] = {
                ...current[idx],
                operationId,
                voteCount: (current[idx].voteCount || 0) + 1,
            };
            return current;
        });
        try {
            await apiTagReportSegment({reportSegmentId, operationId, name, userId: user.uid});
        } catch (e) {
            // Rollback the optimistic vote.
            setTags(prev => {
                const current = Array.isArray(prev) ? prev.map(x => ({...x})) : [];
                const idx = current.findIndex(x => (x.name || '').toLowerCase() === lower);
                if (idx === -1) return current;
                const item = {...current[idx]};
                if (item.operationId !== operationId) return current;
                item.operationId = null;
                item.voteCount = Math.max(0, (item.voteCount || 1) - 1);
                if (item.voteCount === 0) {
                    current.splice(idx, 1);
                    return current;
                }
                current[idx] = item;
                return current;
            });
            throw e;
        } finally {
            setIsPosting(false);
        }
    }

    async function voteTag(tagName) {
        const name = (tagName || '').trim();
        if (!name || !enabled) return;
        const lower = name.toLowerCase();
        if (loadingTagOps[lower]) return;
        const current = tags.find(x => (x.name || '').toLowerCase() === lower);
        const alreadyVoted = !!current?.operationId;
        setLoadingTagOps(prev => ({...prev, [lower]: true}));
        const prevTags = Array.isArray(tags) ? tags.map(x => ({...x})) : [];
        try {
            if (!alreadyVoted) {
                await createTag(name);
                return;
            }
            // Un-vote: optimistic decrement, then delete the viewer's vote server-side.
            setTags(prev => {
                const next = (Array.isArray(prev) ? prev.map(x => ({...x})) : []);
                const idx = next.findIndex(x => (x.name || '').toLowerCase() === lower);
                if (idx === -1) return next;
                const item = {...next[idx], operationId: null};
                item.voteCount = Math.max(0, (item.voteCount || 1) - 1);
                if (item.voteCount === 0) {
                    next.splice(idx, 1);
                    return next;
                }
                next[idx] = item;
                return next;
            });
            await apiRemoveReportSegmentTag({reportSegmentId, tagName: name, userId: user.uid});
        } catch (e) {
            setTags(prevTags);
            throw e;
        } finally {
            setLoadingTagOps(prev => ({...prev, [lower]: false}));
        }
    }

    return {tags, createTag, voteTag, isPosting, loadingTagOps};
}
